import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import globalStyles from '@/styles/index.module.css';
import { FullLoading } from '@components/Loaders';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { useForm } from '@tanstack/react-form';
import { getRouteApi } from '@tanstack/react-router';
import type { ColDef } from 'ag-grid-community';
import clsx from 'clsx';
import React, { type FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { UserActionRow } from './UserActionRow';
import styles from './UserDetails.module.css';
import { UserHeaderCard, type UserEditValues } from './UserHeaderCard';
import { useUserDetail } from './useUserDetail';

// getRouteApi avoids a circular import (UserDetails/index → $userId.lazy → UserDetails/index)
const routeApi = getRouteApi('/users/$userId');

const UserDetails: FC = () => {
  const { userId } = routeApi.useParams();
  const { t: tUsers } = useTranslation('users');
  const { t: tGeneral } = useTranslation('general');

  const { data: user, isPending } = useUserDetail(userId);
  const { data: orgs = [] } = useOrganizations();

  const [isEditing, setIsEditing] = useState(false);

  const form = useForm({
    defaultValues: {
      first_name: user?.full_name?.split(' ')[0] ?? '',
      last_name: user?.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user?.email ?? '',
      is_active: user?.is_active ?? true,
    } satisfies UserEditValues,
    // TODO: implement onSubmit when PATCH /users/{userId} endpoint lands
    onSubmit: async () => {
      setIsEditing(false);
    },
  });

  useEffect(() => {
    if (!user || isEditing) return;
    form.reset({
      first_name: user.full_name?.split(' ')[0] ?? '',
      last_name: user.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user.email ?? '',
      is_active: user.is_active ?? true,
    });
  }, [user, isEditing, form]);

  const handleEdit = useCallback(() => setIsEditing(true), []);

  const handleCancel = useCallback(() => {
    form.reset({
      first_name: user?.full_name?.split(' ')[0] ?? '',
      last_name: user?.full_name?.split(' ').slice(1).join(' ') ?? '',
      email: user?.email ?? '',
      is_active: user?.is_active ?? true,
    });
    setIsEditing(false);
  }, [form, user]);

  const handleSave = useCallback(() => {
    void form.handleSubmit();
  }, [form]);

  const studyColDefs = useMemo<ColDef[]>(
    () => [
      { field: 'can_create_runs', headerName: tUsers('col_create_runs'), maxWidth: 120 },
      { field: 'name', headerName: tGeneral('name') },
      { field: 'description', headerName: tGeneral('desc') },
      { field: 'organization', headerName: tUsers('organization_column_header') },
    ],
    [tUsers, tGeneral],
  );

  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  if (isPending) return <FullLoading label={tUsers('detail_loading')} />;
  if (!user) return <Text>{tUsers('detail_not_found')}</Text>;

  const primaryOrgId = Object.keys(user.allowed_roles ?? {})[0];

  const orgMemberships = Object.entries(user.allowed_roles ?? {}).map(([oId, roles]) => ({
    orgId: oId,
    roles,
    isPrimary: oId === primaryOrgId,
  }));

  return (
    <Stack fullHeight fullWidth orientation="vertical">
      <UserActionRow
        isEditing={isEditing}
        onCancel={handleCancel}
        onEdit={handleEdit}
        onSave={handleSave}
        user={user}
      />

      <Stack className={clsx(styles.content, globalStyles.scrollbars)} gap={4} orientation="vertical">
        <UserHeaderCard form={form} isEditing={isEditing} user={user} />

        {/* Organization Memberships */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          <Text as="h3">{tUsers('org_memberships')}</Text>
          {orgMemberships.map(({ orgId: oId, roles, isPrimary }) => (
            <Stack alignItems="center" gap={4} key={oId} orientation="horizontal">
              <Text as="span">{orgNameMap.get(oId) ?? oId}</Text>
              {roles.map(role => (
                <Badge key={role} size="xs" variant="default">
                  {role}
                </Badge>
              ))}
              {isPrimary && <Text as="span">{tUsers('primary_role_indicator')}</Text>}
            </Stack>
          ))}
        </Stack>

        {/* Studies — stubbed until GET /users/{userId}/studies lands */}
        {/* TODO: wire to GET /users/${userId}/studies when endpoint lands */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          <Text as="h3">{tUsers('study_count', { count: 0 })}</Text>
          <Table columnDefs={studyColDefs} domLayout="autoHeight" rowData={[]} tableId="user-studies-table" />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserDetails;
